import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { AppFrame } from '../components/AppNav';
import { Banner, EmptyState, Loading, Meter, StatusPill } from '../components/ui';
import { formatDateTime, formatExact, pluralize, relativeTime, timeZoneName } from '../lib/format';
import { useSession } from '../state/session';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'active', label: 'Out for signature' },
  { key: 'completed', label: 'Completed' },
  { key: 'voided', label: 'Withdrawn' },
];

export function Dashboard() {
  const { orgId } = useSession();
  const [overview, setOverview] = useState<any>(null);
  const [list, setList] = useState<any>(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    if (!orgId) return;
    const [summary, envelopes] = await Promise.all([
      api.get(`/organizations/${orgId}/overview`),
      api.get(`/organizations/${orgId}/envelopes?status=${filter}` +
              (query ? `&q=${encodeURIComponent(query)}` : '')),
    ]);
    setOverview(summary);
    setList(envelopes);
  }, [orgId, filter, query]);

  useEffect(() => { void load(); }, [load]);

  if (!overview || !list) {
    return <AppFrame><div className="shell section"><Loading /></div></AppFrame>;
  }

  const allowance = overview.allowance;
  const ent = overview.entitlements;

  return (
    <AppFrame>
      <div className="shell section stack-l">
        <header className="row row--between">
          <div>
            <h1 style={{ marginBottom: 4 }}>Agreements</h1>
            <p className="muted small" style={{ margin: 0 }}>
              {pluralize(overview.counts.out_for_signature, 'agreement')} out for signature
              {' · '}{overview.counts.completed} completed
            </p>
          </div>
        </header>

        {ent.status === 'none' && (
          <Banner tone="warn" title="Sending is not active yet">
            Activate the three-day sending trial with a payment method to send
            agreements. You can keep preparing in the meantime.{' '}
            <Link to="/app/billing">Go to billing</Link>.
          </Banner>
        )}

        {ent.status === 'trialing' && (
          <Banner tone="brand" title={`Trial active — first charge ${formatDateTime(ent.first_charge_at)}`}>
            {ent.plan_name} at {ent.price_display} per year will be charged on{' '}
            <strong>{formatExact(ent.first_charge_at)}</strong> ({timeZoneName()}) unless
            you cancel first. Billing begins at trial expiry even if your recipients
            have not finished signing. <Link to="/app/billing">Manage or cancel</Link>.
          </Banner>
        )}

        {ent.status === 'past_due' && (
          <Banner tone="danger" title="Payment failed">
            New sends are paused until payment succeeds. Agreements already sent are
            unaffected and remain retrievable.{' '}
            <Link to="/app/billing">Update payment method</Link>.
          </Banner>
        )}

        {allowance.state === 'credits' && (
          <div className="card stack-s">
            <div className="row row--between">
              <strong style={{ fontSize: '.94rem' }}>Credits</strong>
              <span className="small muted">
                {allowance.balance} credits · {allowance.sends_remaining}{' '}
                {allowance.sends_remaining === 1 ? 'send' : 'sends'}
              </span>
            </div>
            {/* No meter and no reset date: credits have no period, and a bar
                filling up would suggest a deadline that does not exist. */}
            <div className="row row--between">
              <span className="small muted">
                {allowance.credits_per_send} credits per send. Credits do not expire.
              </span>
              {!allowance.sending_enabled && (
                <span className="pill pill--danger">No credits</span>
              )}
            </div>
            {!allowance.sending_enabled && (
              <p className="small muted" style={{ margin: 0 }}>
                {allowance.reason} <Link to="/app/billing">Top up</Link>.
              </p>
            )}
            {allowance.auto_recharge_paused && (
              <p className="small" style={{ margin: 0, color: 'var(--warn)' }}>
                Automatic top-up is paused because a card was declined.{' '}
                <Link to="/app/billing">Fix it in Billing</Link>.
              </p>
            )}
          </div>
        )}

        {allowance.state !== 'inactive' && allowance.state !== 'credits' && (
          <div className="card stack-s">
            <div className="row row--between">
              <strong style={{ fontSize: '.94rem' }}>
                {allowance.state === 'trial' ? 'Trial allowance' : 'This month'}
              </strong>
              <span className="small muted">
                {allowance.used} of {allowance.allowance} used
              </span>
            </div>
            <Meter used={allowance.used} allowance={allowance.allowance} />
            <div className="row row--between">
              <span className="small muted">
                Resets {formatDateTime(allowance.next_reset_at)} ({timeZoneName()})
              </span>
              {allowance.remaining === 0 && (
                <span className="pill pill--danger">Limit reached</span>
              )}
            </div>
            {allowance.remaining === 0 && (
              <p className="small muted" style={{ margin: 0 }}>{allowance.reason}</p>
            )}
            {allowance.notice_reached && allowance.remaining > 0 && (
              <p className="small" style={{ margin: 0, color: 'var(--warn)' }}>
                You have used {allowance.used} of {allowance.allowance} requests.
              </p>
            )}
          </div>
        )}

        {overview.support_tasks.length > 0 && (
          <Banner tone="warn" title="Delivery follow-ups">
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
              {overview.support_tasks.map((task: any) => (
                <li key={task.id}>
                  {task.summary}. {task.action}
                  {task.envelope_id && (
                    <> <Link to={`/app/envelopes/${task.envelope_id}`}>
                      Open the agreement
                    </Link>.</>
                  )}
                </li>
              ))}
            </ul>
          </Banner>
        )}

        <div className="stack">
          <div className="row row--between">
            <div className="row row--tight" role="tablist" aria-label="Filter agreements">
              {FILTERS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === item.key}
                  className={`btn btn--small${filter === item.key ? ' btn--primary' : ' btn--ghost'}`}
                  onClick={() => setFilter(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <label className="sr-only" htmlFor="search">Search agreements</label>
            <input
              id="search"
              className="input"
              style={{ maxWidth: 260 }}
              type="search"
              placeholder="Search by title"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          {list.items.length === 0 ? (
            <EmptyState
              title="Nothing here yet"
              action={<Link className="btn btn--primary" to="/app/new">New agreement</Link>}
            >
              Upload a PDF, place fields and send it for signature.
            </EmptyState>
          ) : (
            <div className="list">
              {list.items.map((envelope: any) => (
                <Link className="listitem" key={envelope.id} to={`/app/envelopes/${envelope.id}`}>
                  <div className="row row--between row--top">
                    <div style={{ minWidth: 0, flex: '1 1 240px' }}>
                      <p className="listitem__title truncate">{envelope.title}</p>
                      <div className="listitem__meta">
                        <span>
                          {envelope.completed_count} of {envelope.recipient_count} signed
                        </span>
                        {envelope.sent_at && <span>Sent {relativeTime(envelope.sent_at)}</span>}
                        {envelope.completed_at && (
                          <span>Completed {relativeTime(envelope.completed_at)}</span>
                        )}
                        {envelope.self_sign_only && <span>Self-signed</span>}
                      </div>
                    </div>
                    <StatusPill status={envelope.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppFrame>
  );
}
